// --- NotificationPlaydateWorkflow.tsx ---

import React from "react";
import {
    Notification,
    PlaydateNotificationMetadata,
    useGetPlaydateInviteQuery,
    User,
} from "gql/types";
import PlaydateInviteRecipientReviewWorkflow from "components/playdate/workflow/recipient/review/PlaydateInviteRecipientReviewWorkflow";
import PlaydateInviteOrganizerReviewWorkflow from "components/playdate/workflow/PlaydateInviteOrganizerReviewWorkflow";
import PlaydateInviteLoadingView from "components/playdate/view/PlaydateInviteLoadingView";
import PortalRoot from "components/portal/PortalRoot";
import { OverlayOpacity } from "components/portal/types";
import { useToastContext } from "contexts/ToastContext";

interface NotificationPlaydateWorkflowProps {
    notification?: Notification;
    inviteID?: string;
    user?: User; // Replace with actual type
    onClose: () => void;
    baseZIndex?: number;
    onDataChange: (success: boolean) => void;
}

const NotificationPlaydateWorkflow: React.FC<NotificationPlaydateWorkflowProps> = ({
    notification,
    inviteID,
    user,
    onClose,
    baseZIndex,
    onDataChange,
}) => {
    const { pushToastEvent, triggerToastEvent } = useToastContext();

    let resolvedInviteID: string | undefined = inviteID;

    if (!resolvedInviteID && notification?.metadata?.__typename === "PlaydateNotificationMetadata") {
        const metadata = notification.metadata as PlaydateNotificationMetadata;
        resolvedInviteID = metadata.inviteID;
    }

    const { data, loading, error } = useGetPlaydateInviteQuery({
        skip: !resolvedInviteID,
        variables: { inviteID: resolvedInviteID ?? "" },
        fetchPolicy: "cache-and-network",
    });

    if (!resolvedInviteID) {
        pushToastEvent({
            eventType: "error",
            message: "Unable to open invite — missing ID.",
        });
        triggerToastEvent();
        onDataChange(false);
        onClose();
        return <></>;
    }

    const invite = data?.getPlaydateInvite;

    if (loading || !invite) {
        return (
            <PortalRoot overlayOpacity={OverlayOpacity.COVERED} baseZIndex={baseZIndex}>
                <PlaydateInviteLoadingView onBack={onClose} />
            </PortalRoot>);
    }

    if (
        error ||
        (!invite.guestUpdate || invite.guestUpdate === null) &&
        (!invite.organizerUpdate || invite.organizerUpdate === null)
    ) {
        pushToastEvent({
            eventType: "error",
            message: "invite loading failed, please try again later.",
        })
        triggerToastEvent();
        onDataChange(false);
        onClose();
        return <></>;
    }

    const isInbound = Boolean(invite.guestUpdate);

    if (isInbound) {
        return (
            <PlaydateInviteRecipientReviewWorkflow
                user={user}
                onBack={onClose}
                invite={invite}
                onDataChange={onDataChange}
                baseZIndex={baseZIndex}
            />
        );
    } else {
        return (
            <PlaydateInviteOrganizerReviewWorkflow
                onBack={onClose}
                invite={invite}
                onDataChange={onDataChange}
                baseZIndex={baseZIndex}
            />
        );
    }
};

export default NotificationPlaydateWorkflow;
